import { Link } from 'react-router-dom';

export default function FormsDocPage() {
  return (
    <div className="min-h-screen bg-base-100">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-primary mb-6">Forms Documentation</h1>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Overview</h2>
          <p className="text-lg text-base-content mb-4">
            TBit Forms is a powerful form builder and renderer that allows you to create dynamic,
            embeddable forms with advanced field types, validation, and integrations.
          </p>
        </section>

        {/* Supported Field Types */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Supported Field Types</h2>

          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th>Field Type</th>
                  <th>Description</th>
                  <th>Validation</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>TEXT</code>
                  </td>
                  <td>Single-line text input</td>
                  <td>Required, min/max length</td>
                </tr>
                <tr>
                  <td>
                    <code>EMAIL</code>
                  </td>
                  <td>Email address input</td>
                  <td>Email format validation</td>
                </tr>
                <tr>
                  <td>
                    <code>TEL</code>
                  </td>
                  <td>Phone number input</td>
                  <td>Phone format validation</td>
                </tr>
                <tr>
                  <td>
                    <code>URL</code>
                  </td>
                  <td>Website URL input</td>
                  <td>URL format validation</td>
                </tr>
                <tr>
                  <td>
                    <code>NUMBER</code>
                  </td>
                  <td>Numeric input</td>
                  <td>Number validation, min/max</td>
                </tr>
                <tr>
                  <td>
                    <code>TEXTAREA</code>
                  </td>
                  <td>Multi-line text input</td>
                  <td>Required, max length</td>
                </tr>
                <tr>
                  <td>
                    <code>SELECT</code>
                  </td>
                  <td>Dropdown selection</td>
                  <td>Required, valid option</td>
                </tr>
                <tr>
                  <td>
                    <code>RADIO</code>
                  </td>
                  <td>Radio button group</td>
                  <td>Required, single selection</td>
                </tr>
                <tr>
                  <td>
                    <code>CHECKBOX</code>
                  </td>
                  <td>Checkbox group</td>
                  <td>Multiple selections</td>
                </tr>
                <tr>
                  <td>
                    <code>DATE</code>
                  </td>
                  <td>Date picker</td>
                  <td>Valid date format</td>
                </tr>
                <tr>
                  <td>
                    <code>DATETIME-LOCAL</code>
                  </td>
                  <td>Date and time picker</td>
                  <td>Valid datetime format</td>
                </tr>
                <tr>
                  <td>
                    <code>TIME</code>
                  </td>
                  <td>Time picker</td>
                  <td>Valid time format</td>
                </tr>
                <tr>
                  <td>
                    <code>BOOLEAN</code>
                  </td>
                  <td>Toggle switch</td>
                  <td>True/false value</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Form Schema */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Form Schema</h2>

          <p className="text-base-content mb-4">
            Forms follow the <code>@tbit-io/types</code> schema structure:
          </p>

          <div className="mockup-code mb-4">
            <pre data-prefix="1">
              <code>{'interface Form {'}</code>
            </pre>
            <pre data-prefix="2">
              <code>{'  agentId: ObjectId;'}</code>
            </pre>
            <pre data-prefix="3">
              <code>{'  name: string;'}</code>
            </pre>
            <pre data-prefix="4">
              <code>{'  description: string;'}</code>
            </pre>
            <pre data-prefix="5">
              <code>{'  fields: FormField[];'}</code>
            </pre>
            <pre data-prefix="6">
              <code>{'  status: "DRAFT" | "PUBLISHED" | "ARCHIVED" | "DESACTIVATED";'}</code>
            </pre>
            <pre data-prefix="7">
              <code>{'  created_at: Date;'}</code>
            </pre>
            <pre data-prefix="8">
              <code>{'  updated_at: Date;'}</code>
            </pre>
            <pre data-prefix="9">
              <code>{'}'}</code>
            </pre>
          </div>

          <div className="mockup-code">
            <pre data-prefix="1">
              <code>{'interface StandardField {'}</code>
            </pre>
            <pre data-prefix="2">
              <code>{'  id: string;'}</code>
            </pre>
            <pre data-prefix="3">
              <code>{'  type: "STANDARD";'}</code>
            </pre>
            <pre data-prefix="4">
              <code>{'  name: string;'}</code>
            </pre>
            <pre data-prefix="5">
              <code>{'  label: string;'}</code>
            </pre>
            <pre data-prefix="6">
              <code>{'  inputType: "TEXT" | "EMAIL" | "NUMBER" | ...;'}</code>
            </pre>
            <pre data-prefix="7">
              <code>{'  required: boolean;'}</code>
            </pre>
            <pre data-prefix="8">
              <code>{'  requestFromUser: boolean;'}</code>
            </pre>
            <pre data-prefix="9">
              <code>{'  placeholder?: string;'}</code>
            </pre>
            <pre data-prefix="10">
              <code>{'  description?: string;'}</code>
            </pre>
            <pre data-prefix="11">
              <code>{'  options?: string[];'}</code>
            </pre>
            <pre data-prefix="12">
              <code>{'}'}</code>
            </pre>
          </div>
        </section>

        {/* Customization */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Customization</h2>

          <div className="space-y-4">
            <div className="card bg-base-200">
              <div className="card-body">
                <h3 className="card-title text-lg">Theme Customization</h3>
                <p className="text-base-content/80">
                  Forms automatically adapt to your chosen theme (light or dark). You can switch
                  themes using the <code>data-theme</code> attribute.
                </p>
              </div>
            </div>

            <div className="card bg-base-200">
              <div className="card-body">
                <h3 className="card-title text-lg">Size Customization</h3>
                <p className="text-base-content/80">
                  Control the form dimensions using <code>data-width</code> and{' '}
                  <code>data-height</code> attributes. Use &ldquo;auto&rdquo; for responsive sizing.
                </p>
              </div>
            </div>

            <div className="card bg-base-200">
              <div className="card-body">
                <h3 className="card-title text-lg">Custom Styling</h3>
                <p className="text-base-content/80">
                  Forms are built with TBit&apos;s design system. All components follow the brand
                  guidelines and are fully responsive.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Best Practices</h2>

          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="badge badge-primary">✓</div>
              <div>
                <strong>Use appropriate field types</strong> - Choose the right input type for
                better UX and built-in validation
              </div>
            </div>
            <div className="flex gap-3">
              <div className="badge badge-primary">✓</div>
              <div>
                <strong>Provide clear labels</strong> - Make sure field labels are descriptive and
                easy to understand
              </div>
            </div>
            <div className="flex gap-3">
              <div className="badge badge-primary">✓</div>
              <div>
                <strong>Add helpful descriptions</strong> - Use the description field to provide
                additional context
              </div>
            </div>
            <div className="flex gap-3">
              <div className="badge badge-primary">✓</div>
              <div>
                <strong>Mark required fields</strong> - Clearly indicate which fields are required
              </div>
            </div>
            <div className="flex gap-3">
              <div className="badge badge-primary">✓</div>
              <div>
                <strong>Test on mobile</strong> - Ensure your forms work well on all device sizes
              </div>
            </div>
            <div className="flex gap-3">
              <div className="badge badge-primary">✓</div>
              <div>
                <strong>Handle submissions</strong> - Implement proper success/error handling
              </div>
            </div>
          </div>
        </section>

        {/* Examples */}
        <section>
          <h2 className="text-3xl font-bold text-primary mb-4">Examples</h2>

          <div className="space-y-4">
            <Link
              to="/forms/demo"
              className="card bg-primary text-primary-content hover:shadow-xl transition-shadow"
            >
              <div className="card-body">
                <h3 className="card-title">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  View Live Demo
                </h3>
                <p>See all field types in action with our interactive demo form</p>
              </div>
            </Link>

            <Link
              to="/docs/getting-started"
              className="card bg-secondary text-secondary-content hover:shadow-xl transition-shadow"
            >
              <div className="card-body">
                <h3 className="card-title">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  Quick Start Guide
                </h3>
                <p>Get up and running in minutes with our step-by-step guide</p>
              </div>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
